import { Check, ArrowUpRight, Briefcase, HeartPulse, Scale, Smartphone, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { CAREERS_LOOKING, LS_ASSOCIATE_PERKS, KMD_AMBASSADOR_PERKS, LS, KMD } from "@/data";
import { ATT_REP_ROLES, ATT_BLUE } from "@/attData";
import { TAXPRO_APPLY_URL } from "@/pages/TaxProPage";

export default function CareersPage() {
  return (
    <>
      {/* Cinematic careers hero — royal blue & gold spotlight */}
      <section className="relative pt-20 md:pt-24 overflow-hidden" data-testid="careers-hero">
        <div className="relative">
          <img
            src="/careers-hero.png"
            alt="Heritage Family Protection Agency — Careers"
            data-testid="careers-hero-image"
            className="w-full h-auto"
          />
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#081226] to-transparent" />
        </div>
        <div className="relative -mt-6 pb-12" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.12), rgba(8,18,38,0) 60%)" }}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <Reveal>
              <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Careers · Join The Industry</p>
              <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white">
                Build a career while <span className="text-gold-gradient italic">protecting families.</span>
              </h1>
              <p className="mt-5 mx-auto max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/60">
                Heritage offers five powerful ways to build your business — as a licensed Financial Services Agent, an
                independent LegalShield Associate, a KonnectMD Ambassador, an AT&amp;T Sales Representative, or a Tax
                Professional. Choose your path, or do them all.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Path selector */}
      <section className="relative pb-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Reveal>
            <a href="#agent" className="group flex items-center justify-between rounded-2xl border border-[#bf953f]/25 bg-[#0a192f] p-6 hover:border-[#d4af37]/60 transition-colors duration-500">
              <div className="flex items-center gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#bf953f]/30 text-[#d4af37]"><Briefcase size={22} strokeWidth={1.5} /></span>
                <div>
                  <p className="font-serif-display text-xl text-white">Financial Services Agent</p>
                  <p className="font-body text-sm text-white/50">Heritage Family Protection Agency</p>
                </div>
              </div>
              <ArrowUpRight size={20} className="shrink-0 text-[#d4af37] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
          <Reveal delay={0.06}>
            <a href="#associate" className="group flex items-center justify-between rounded-2xl border bg-[#0a192f] p-6 transition-colors duration-500" style={{ borderColor: "rgba(130,49,212,0.35)" }}>
              <div className="flex items-center gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ border: "1px solid rgba(130,49,212,0.4)", color: "#b98bff" }}><Scale size={22} strokeWidth={1.5} /></span>
                <div>
                  <p className="font-serif-display text-xl text-white">LegalShield Associate</p>
                  <p className="font-body text-sm text-white/50">Independent business opportunity</p>
                </div>
              </div>
              <ArrowUpRight size={20} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" style={{ color: "#b98bff" }} />
            </a>
          </Reveal>
          <Reveal delay={0.12}>
            <a href="#konnectmd" className="group flex items-center justify-between rounded-2xl border bg-[#0a192f] p-6 transition-colors duration-500" style={{ borderColor: "rgba(17,115,185,0.4)" }}>
              <div className="flex items-center gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ border: "1px solid rgba(17,115,185,0.45)", color: "#6db6e8" }}><HeartPulse size={22} strokeWidth={1.5} /></span>
                <div>
                  <p className="font-serif-display text-xl text-white">KonnectMD Ambassador</p>
                  <p className="font-body text-sm text-white/50">Share virtual healthcare & earn</p>
                </div>
              </div>
              <ArrowUpRight size={20} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" style={{ color: "#6db6e8" }} />
            </a>
          </Reveal>
          <Reveal delay={0.18}>
            <a href="#att-careers" data-testid="careers-path-att" className="group flex items-center justify-between rounded-2xl border bg-[#0a192f] p-6 transition-colors duration-500" style={{ borderColor: "rgba(0,168,224,0.4)" }}>
              <div className="flex items-center gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ border: "1px solid rgba(0,168,224,0.45)", color: ATT_BLUE }}><Smartphone size={22} strokeWidth={1.5} /></span>
                <div>
                  <p className="font-serif-display text-xl text-white">AT&T Sales Rep</p>
                  <p className="font-body text-sm text-white/50">Door to door, events, retail, B2B & remote</p>
                </div>
              </div>
              <ArrowUpRight size={20} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" style={{ color: ATT_BLUE }} />
            </a>
          </Reveal>
          <Reveal delay={0.24}>
            <a href="#tax-pro" data-testid="careers-path-taxpro" className="group flex items-center justify-between rounded-2xl border border-[#bf953f]/25 bg-[#0a192f] p-6 hover:border-[#d4af37]/60 transition-colors duration-500">
              <div className="flex items-center gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#bf953f]/30 text-[#d4af37]"><Calculator size={22} strokeWidth={1.5} /></span>
                <div>
                  <p className="font-serif-display text-xl text-white">Tax Professional</p>
                  <p className="font-body text-sm text-white/50">Prepare returns & build seasonal income</p>
                </div>
              </div>
              <ArrowUpRight size={20} className="shrink-0 text-[#d4af37] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* AGENT TRACK */}
      <section id="agent" className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <img src="/logo-full.png" alt="Heritage Family Protection Agency" data-testid="careers-path-one-logo" className="h-16 md:h-20 w-auto object-contain" />
            <p className="mt-6 font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Path One</p>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Become a <span className="text-gold-gradient italic">Financial Services Agent.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/60">
              Join Heritage Family Protection Agency and help families protect their income, homes, health, and legacy.
              We're looking for motivated, coachable, ethical people ready to build a meaningful career in financial
              services — with training and licensing guidance available for qualified candidates.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {CAREERS_LOOKING.map((c, i) => (
              <Reveal key={i} delay={(i % 2) * 0.04}>
                <div className="flex items-start gap-3 border-t border-white/10 pt-4">
                  <Check size={17} className="mt-0.5 shrink-0 text-[#d4af37]" />
                  <span className="font-body text-white/70 leading-relaxed">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12 rounded-2xl border border-white/10 bg-[#0a192f] p-8">
              <p className="font-body font-light text-base leading-relaxed text-white/55">
                Applicants must generally be at least 18, be willing to complete a background check, meet applicable
                state licensing requirements, and complete all required training. This is a performance-based business
                opportunity — not a guaranteed hourly or salaried position. Compensation depends on licensing, business
                activity, performance, and carrier requirements.
              </p>
              <Link
                to="/careers/apply"
                data-testid="careers-agent-apply"
                className="btn-sweep mt-7 inline-flex items-center gap-2 rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-black"
              >
                Apply to Join Heritage
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LEGALSHIELD ASSOCIATE TRACK */}
      <section id="associate" className="relative py-20 md:py-28 border-y border-white/10" style={{ background: "linear-gradient(180deg, rgba(130,49,212,0.12) 0%, #0a192f 60%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <img src={LS.logo} alt="LegalShield" className="h-12 md:h-14 w-auto object-contain" />
            <p className="mt-6 font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: "#b98bff" }}>Path Two</p>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Become a <span className="italic" style={{ color: "#b98bff" }}>LegalShield Associate.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/65">
              Start your own business helping families and small businesses access affordable legal protection and
              identity theft coverage. As an independent LegalShield Associate on our team, you set your own pace, earn
              on your own terms, and get the training and mentorship to grow — no experience required.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {LS_ASSOCIATE_PERKS.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 0.04}>
                <div className="flex items-start gap-3 border-t pt-4" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                  <Check size={17} className="mt-0.5 shrink-0" style={{ color: "#b98bff" }} />
                  <span className="font-body text-white/75 leading-relaxed">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.12}>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href={LS.associate}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="careers-associate-join"
                className="btn-sweep group inline-flex items-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold tracking-wide text-white shadow-[0_10px_40px_-8px_rgba(130,49,212,0.7)] transition-transform duration-300 hover:scale-[1.03]"
                style={{ backgroundColor: LS.purple }}
              >
                Join My Team
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <p className="font-body text-xs text-white/35 max-w-md leading-relaxed">
                Independent business opportunity. Earnings depend on effort, results, and applicable agreements.
                LegalShield is a product of Pre-Paid Legal Services, Inc.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* KONNECTMD AMBASSADOR TRACK */}
      <section id="konnectmd" className="relative py-20 md:py-28 border-b border-white/10" style={{ background: "linear-gradient(180deg, rgba(17,115,185,0.12) 0%, #0a192f 60%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="inline-flex items-center rounded-xl bg-white px-5 py-3">
              <img src={KMD.logo} alt="KonnectMD" className="h-9 md:h-10 w-auto object-contain" />
            </div>
            <p className="mt-6 font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: "#6db6e8" }}>Path Three</p>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Become a <span className="italic" style={{ color: "#6db6e8" }}>KonnectMD Ambassador.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/65">
              Millions of families and small businesses are priced out of traditional healthcare. As a KonnectMD
              Ambassador, you share affordable 24/7 virtual healthcare memberships — with free prescriptions and
              $0 copays — and build recurring income doing it. No license required, and it pairs perfectly with
              the Heritage agent and LegalShield associate paths.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {KMD_AMBASSADOR_PERKS.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 0.04}>
                <div className="flex items-start gap-3 border-t pt-4" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                  <Check size={17} className="mt-0.5 shrink-0" style={{ color: "#6db6e8" }} />
                  <span className="font-body text-white/75 leading-relaxed">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.12}>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href={KMD.ambassador}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="careers-konnectmd-join"
                className="btn-sweep group inline-flex items-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold tracking-wide text-white shadow-[0_10px_40px_-8px_rgba(17,115,185,0.7)] transition-transform duration-300 hover:scale-[1.03]"
                style={{ backgroundColor: KMD.blue }}
              >
                Become an Ambassador
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <p className="font-body text-xs text-white/35 max-w-md leading-relaxed">
                Independent ambassador opportunity. Earnings depend on effort, results, and applicable agreements.
                KonnectMD is an independent telehealth service.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AT&T SALES REP TRACK */}
      <section id="att-careers" className="relative py-20 md:py-28 border-b border-white/10" style={{ background: "linear-gradient(180deg, rgba(0,168,224,0.14) 0%, #0a192f 60%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-wrap items-center gap-4">
              <img src="/logo-full.png" alt="Heritage Family Protection Agency" className="h-16 md:h-20 w-auto object-contain" data-testid="att-careers-heritage-logo" />
              <div className="inline-flex items-center rounded-xl bg-white overflow-hidden">
                <img src="/att/att-authorized-dealer.jpg" alt="AT&T Authorized Dealer" className="h-16 md:h-20 w-auto object-contain" />
              </div>
            </div>
            <p className="mt-6 font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: ATT_BLUE }}>Path Four</p>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Become an <span className="italic" style={{ color: ATT_BLUE }}>AT&amp;T Sales Rep.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/65">
              As an AT&amp;T Authorized Dealer, our team sells the biggest name in wireless and fiber — and we're
              hiring reps for five different lanes. Uncapped commissions, full training, and real products people
              already want. Pick the lane that fits your life.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="att-careers-roles">
            {ATT_REP_ROLES.map((r, i) => (
              <Reveal key={r.id} delay={(i % 2) * 0.05}>
                <div data-testid={`att-role-${r.id}`} className="h-full flex flex-col rounded-2xl border bg-[#0a192f] p-7" style={{ borderColor: "rgba(0,168,224,0.3)" }}>
                  <h3 className="font-serif-display text-2xl text-white">{r.name}</h3>
                  <p className="mt-3 font-body font-light text-sm md:text-base leading-relaxed text-white/60">{r.desc}</p>
                  <div className="mt-6 pt-2">
                    <Link
                      to={`/careers/att-apply?role=${r.id}`}
                      data-testid={`att-role-apply-${r.id}`}
                      className="btn-sweep inline-flex items-center gap-2 rounded-full px-7 py-3 font-body text-sm font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
                      style={{ backgroundColor: ATT_BLUE }}
                    >
                      Click Here to Apply
                      <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-8 font-body text-xs text-white/35 max-w-2xl leading-relaxed">
              Independent, performance-based sales opportunity through Heritage Family Protection Agency, an AT&amp;T
              Authorized Dealer via DM Wireless Connect. Earnings depend on effort and results. Background check and
              drug screening required for most positions.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TAX PROFESSIONAL TRACK */}
      <section id="tax-pro" className="relative py-20 md:py-28 border-b border-white/10" style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.10) 0%, #0a192f 60%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Path Five</p>
              <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
                Become a <span className="text-gold-gradient italic">Tax Professional.</span>
              </h2>
              <p className="mt-5 max-w-2xl font-body font-light text-base md:text-lg leading-relaxed text-white/65">
                Help families and small businesses file with confidence, claim every credit they deserve, and keep more
                of what they earn. We provide complete onboarding, training, professional software, and mentorship
                through our Top Dollar Tax partnership — no prior tax experience required. Build serious seasonal income
                that pairs perfectly with every other Heritage path.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href={TAXPRO_APPLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="careers-taxpro-apply"
                  className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-8 py-4 font-body text-sm font-semibold tracking-wide text-black"
                >
                  Apply Now <ArrowUpRight size={16} />
                </a>
                <Link
                  to="/careers/tax-pro"
                  data-testid="careers-taxpro-details"
                  className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 px-8 py-4 font-body text-sm tracking-wide text-white/80 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
                >
                  View the Full Role
                </Link>
              </div>
              <p className="mt-6 font-body text-xs text-white/35 max-w-md leading-relaxed">
                Performance-based opportunity. Earnings depend on effort, results, and applicable agreements. Onboarding
                handled through a secure application form.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.08}>
              <Link to="/careers/tax-pro" className="block overflow-hidden rounded-3xl border border-[#d4af37]/30 shadow-[0_0_45px_rgba(212,175,55,0.12)]">
                <img
                  src="/careers/taxpro-banner.jpg"
                  alt="Tax Professional career — desk with tax documents under gold spotlight"
                  data-testid="careers-taxpro-image"
                  className="w-full h-72 md:h-96 object-cover transition-transform duration-500 hover:scale-[1.03]"
                />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
